import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogIn, UserPlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

/**
 * Premium sign-in / register nudge shown when a Guest attempts an
 * account-owned action. Rendered once at the app root; components call
 * `useAuthGate().require(onProceed)` to gate a protected action.
 */

const AuthGateContext = createContext(null);
export const useAuthGate = () => useContext(AuthGateContext);

export function AuthGateProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null); // { onProceed: fn, redirectTo: string }
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * require(onProceed?, redirectTo?)
   * If authed → runs onProceed immediately.
   * Otherwise → opens the popup; the intended action fires after successful
   * authentication (or user cancels).
   */
  const require = useCallback((onProceed, redirectTo, options) => {
    if (isAuthenticated) {
      onProceed && onProceed();
      return true;
    }
    setPending({
      onProceed,
      redirectTo: redirectTo || location.pathname + location.search + location.hash,
      title: options?.title,
      body: options?.body,
    });
    setOpen(true);
    return false;
  }, [isAuthenticated, location]);

  // After the user authenticates, resume the pending action.
  useEffect(() => {
    if (isAuthenticated && pending) {
      const p = pending;
      setPending(null); setOpen(false);
      // Give AuthContext a tick to finish spreading the new user before firing.
      setTimeout(() => { try { p.onProceed && p.onProceed(); } catch { /* */ } }, 50);
    }
  }, [isAuthenticated, pending]);

  const goSignIn = () => {
    const to = pending?.redirectTo || location.pathname + location.search + location.hash;
    navigate("/login", { state: { from: { pathname: to } } });
    setOpen(false);
  };
  const goRegister = () => {
    const to = pending?.redirectTo || location.pathname + location.search + location.hash;
    navigate("/register", { state: { from: { pathname: to } } });
    setOpen(false);
  };
  const continueGuest = () => { setPending(null); setOpen(false); };

  return (
    <AuthGateContext.Provider value={{ require, open, close: continueGuest }}>
      {children}
      <Dialog open={open} onOpenChange={(v) => { if (!v) continueGuest(); }}>
        <DialogContent
          data-testid="auth-gate-dialog"
          aria-describedby="auth-gate-body"
          className="max-w-md bg-matte-card border-gold/30 text-white rounded-none"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-widest text-white text-lg">
              {pending?.title || "Save Your Work"}
            </DialogTitle>
          </DialogHeader>
          <p id="auth-gate-body" className="text-white/70 text-sm leading-relaxed">
            {pending?.body || "Your drafts live here in your browser. Create a free Songwriter Square account to permanently save your songs, profile and media."}
          </p>
          <div className="mt-2 flex flex-col gap-2">
            <Button onClick={goRegister} data-testid="auth-gate-register"
              className="rounded-none h-11 bg-goldSheen text-black border border-gold/60 font-display uppercase tracking-widest text-xs">
              <UserPlus className="h-3.5 w-3.5 mr-2" /> Create Free Account
            </Button>
            <Button onClick={goSignIn} data-testid="auth-gate-signin" variant="outline"
              className="rounded-none h-11 bg-transparent border-gold/40 text-gold hover:bg-gold/10 hover:border-gold font-display uppercase tracking-widest text-xs">
              <LogIn className="h-3.5 w-3.5 mr-2" /> Sign In
            </Button>
            <div className="flex gap-2 pt-1">
              <Button onClick={continueGuest} data-testid="auth-gate-continue-guest" variant="ghost"
                className="flex-1 rounded-none h-9 text-white/60 hover:text-white text-[11px] uppercase tracking-widest font-display">
                Continue Exploring
              </Button>
              <Button onClick={continueGuest} data-testid="auth-gate-close" variant="ghost"
                className="rounded-none h-9 w-9 text-white/60 hover:text-white flex items-center justify-center" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AuthGateContext.Provider>
  );
}
